import type {
  BackupRun,
  SecurityEvent,
  SupportAccessGrant,
  SupportAccessSession,
  WebhookEndpoint
} from "@kuroo/contracts";
import type { WebhookDeliverySummary } from "./client-auth-types";

export type SdkOperationsContext = {
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
};

export function webhookEndpoints(context: SdkOperationsContext): Promise<WebhookEndpoint[]> {
  return context.request<WebhookEndpoint[]>("/api/v1/webhooks/endpoints");
}

export function createWebhookEndpoint(context: SdkOperationsContext, input: { url: string; events?: string[] }): Promise<{ endpoint: WebhookEndpoint; secret: string }> {
  return context.request<{ endpoint: WebhookEndpoint; secret: string }>("/api/v1/webhooks/endpoints", { method: "POST", body: JSON.stringify(input) });
}

export function updateWebhookEndpoint(context: SdkOperationsContext, endpointId: string, input: { url?: string; events?: string[]; status?: "active" | "paused" }): Promise<WebhookEndpoint> {
  return context.request<WebhookEndpoint>(`/api/v1/webhooks/endpoints/${encodeURIComponent(endpointId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function rotateWebhookEndpoint(context: SdkOperationsContext, endpointId: string): Promise<{ id: string; secret: string }> {
  return context.request<{ id: string; secret: string }>(`/api/v1/webhooks/endpoints/${encodeURIComponent(endpointId)}/rotate`, { method: "POST", body: "{}" });
}

export function deleteWebhookEndpoint(context: SdkOperationsContext, endpointId: string): Promise<{ id: string; deleted: boolean }> {
  return context.request<{ id: string; deleted: boolean }>(`/api/v1/webhooks/endpoints/${encodeURIComponent(endpointId)}`, { method: "DELETE" });
}

export function webhookDeliveries(context: SdkOperationsContext): Promise<WebhookDeliverySummary[]> {
  return context.request<WebhookDeliverySummary[]>("/api/v1/webhooks/deliveries");
}

export function supportAccess(context: SdkOperationsContext): Promise<SupportAccessSession[]> {
  return context.request<SupportAccessSession[]>("/api/v1/admin/support-access");
}

export function requestSupportAccess(context: SdkOperationsContext, input: { reason: string; scopes?: string[]; durationSeconds?: number }): Promise<Partial<SupportAccessSession> & { id: string }> {
  return context.request<Partial<SupportAccessSession> & { id: string }>("/api/v1/support-access/requests", { method: "POST", body: JSON.stringify(input) });
}

export function approveSupportAccess(context: SdkOperationsContext, requestId: string): Promise<SupportAccessGrant> {
  return context.request<SupportAccessGrant>(`/api/v1/support-access/${encodeURIComponent(requestId)}/approve`, { method: "POST", body: "{}" });
}

export function revokeSupportAccess(context: SdkOperationsContext, requestId: string): Promise<{ id: string; status: string }> {
  return context.request<{ id: string; status: string }>(`/api/v1/support-access/${encodeURIComponent(requestId)}/revoke`, { method: "POST", body: "{}" });
}

export function backups(context: SdkOperationsContext, options: { limit?: number; kind?: "scheduled" | "manual" | "restore_test"; status?: "queued" | "running" | "completed" | "failed"; from?: string; to?: string; beforeStartedAt?: string; beforeId?: string } = {}): Promise<BackupRun[]> {
  const query = new URLSearchParams();
  if (options.limit !== undefined) query.set("limit", String(options.limit));
  if (options.kind) query.set("kind", options.kind);
  if (options.status) query.set("status", options.status);
  if (options.from) query.set("from", options.from);
  if (options.to) query.set("to", options.to);
  if (options.beforeStartedAt) query.set("beforeStartedAt", options.beforeStartedAt);
  if (options.beforeId) query.set("beforeId", options.beforeId);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return context.request<BackupRun[]>(`/api/v1/admin/backups${suffix}`);
}

export function requestBackup(context: SdkOperationsContext, kind: "manual" | "restore_test" = "manual"): Promise<Pick<BackupRun, "id" | "kind" | "status">> {
  return context.request<Pick<BackupRun, "id" | "kind" | "status">>("/api/v1/admin/backups", { method: "POST", body: JSON.stringify({ kind }) });
}

export function operationalHealth(context: SdkOperationsContext): Promise<Record<string, unknown>> {
  return context.request<Record<string, unknown>>("/api/v1/ops/health");
}

export function securityEvents(context: SdkOperationsContext, options: { limit?: number; type?: string; severity?: "info" | "warning" | "critical"; from?: string; to?: string; beforeCreatedAt?: string; beforeId?: string } = {}): Promise<SecurityEvent[]> {
  const query = new URLSearchParams();
  if (options.limit !== undefined) query.set("limit", String(options.limit));
  if (options.type) query.set("type", options.type);
  if (options.severity) query.set("severity", options.severity);
  if (options.from) query.set("from", options.from);
  if (options.to) query.set("to", options.to);
  if (options.beforeCreatedAt) query.set("beforeCreatedAt", options.beforeCreatedAt);
  if (options.beforeId) query.set("beforeId", options.beforeId);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return context.request<SecurityEvent[]>(`/api/v1/admin/security-events${suffix}`);
}

export function auditIntegrity(context: SdkOperationsContext): Promise<{ valid: boolean; checked: number; legacyUnsealed: number; invalid: Array<{ id: string; reason: string }>; headHash: string | null; checkedAt: string }> {
  return context.request<{ valid: boolean; checked: number; legacyUnsealed: number; invalid: Array<{ id: string; reason: string }>; headHash: string | null; checkedAt: string }>("/api/v1/admin/audit/verify");
}

export function sendTrackingEvent(context: SdkOperationsContext, provider: "analytics" | "pixel", input: { eventName: string; eventId?: string; clientId?: string; timestamp?: string; sourceUrl?: string; valueCents?: number; currency?: string; userEmail?: string; customData?: Record<string, string | number | boolean>; idempotencyKey: string }): Promise<{ provider: string; accepted: boolean; eventId: string | null; providerEventId: string | null; sentAt: string }> {
  return context.request<{ provider: string; accepted: boolean; eventId: string | null; providerEventId: string | null; sentAt: string }>(`/api/v1/integrations/${provider}/events`, { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": input.idempotencyKey } });
}

export function audit(context: SdkOperationsContext, options: { limit?: number; from?: string; to?: string; beforeCreatedAt?: string; beforeId?: string } = {}): Promise<unknown[]> {
  const query = new URLSearchParams();
  if (options.limit !== undefined) query.set("limit", String(options.limit));
  if (options.from) query.set("from", options.from);
  if (options.to) query.set("to", options.to);
  if (options.beforeCreatedAt) query.set("beforeCreatedAt", options.beforeCreatedAt);
  if (options.beforeId) query.set("beforeId", options.beforeId);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return context.request<unknown[]>(`/api/v1/audit${suffix}`);
}
